import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useDraftCountsBySite } from '@/hooks/useDraftCountsBySite';
import { IconSearch } from '@/lib/icons';
import type { Site } from '@/types';

// SiteSwitcherMenu is the searchable site-switcher dropdown. Extracted from
// Breadcrumb.tsx so the header breadcrumb, the Cmd+K shortcut, and (later) the
// rail site switcher all share one implementation. The caller owns when the
// menu is open and what a selection does; the menu owns the search input,
// keyboard navigation, draft-count pills, and outside-click / Escape close.

interface SiteSwitcherMenuProps {
  open: boolean;
  // onClose: fired on Escape, outside-click, or backdrop dismissal. The
  // caller owns the `open` state.
  onClose: () => void;
  // sites: the selectable sites. The caller filters out decommissioned ones.
  sites: Site[];
  currentSlug: string | null;
  // onSelect: called with the chosen slug (from click or Enter). The caller
  // decides whether to switch (only if different) and to close.
  onSelect: (slug: string) => void;
  // onManageSites: footer "Manage sites" action. The caller closes + navigates.
  onManageSites: () => void;
  // triggerRef: excluded from the outside-click-to-close check so clicking the
  // trigger toggles the menu rather than immediately re-closing it.
  triggerRef?: RefObject<HTMLElement>;
}

export function SiteSwitcherMenu({
  open,
  onClose,
  sites,
  currentSlug,
  onSelect,
  onManageSites,
  triggerRef,
}: SiteSwitcherMenuProps) {
  const [query, setQuery] = useState('');
  const [focusIndex, setFocusIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const filtered = useMemo(() => filterSites(sites, query), [sites, query]);
  // Draft counts fuel the per-row "N drafts" pill. The hook only triggers a
  // fetch the first time the dropdown opens for a given session (cached
  // module-scope for 60s).
  const draftCounts = useDraftCountsBySite(open ? sites : []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setFocusIndex(0);
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      const trigger = triggerRef?.current;
      if (trigger && trigger.contains(t)) return;
      const panel = panelRef.current;
      if (panel && panel.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((i) => Math.min(filtered.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        const target = filtered[focusIndex];
        if (!target) return;
        onSelect(target.slug);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, filtered, focusIndex, onClose, onSelect, triggerRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      id="breadcrumb-switcher-panel"
      ref={panelRef}
      role="dialog"
      aria-label="Switch site"
      className="fixed inset-0 z-50 flex items-start justify-center pt-20"
    >
      {/* Backdrop. Clicking it dismisses the menu. The document-level
          mousedown handler can't catch this — the backdrop is inside
          panelRef, so it counts as "inside the panel" — hence the explicit
          onClick here. Keyboard dismissal is covered by Escape. */}
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-md border border-dash-border bg-dash-surface shadow-xl overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-dash-border">
          <IconSearch size={14} className="text-dash-muted" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setFocusIndex(0);
            }}
            placeholder="Find a site"
            className="flex-1 bg-transparent text-sm text-dash-primary placeholder:text-dash-muted focus:outline-none"
            aria-label="Find a site"
          />
          <kbd className="text-[10px] font-mono text-dash-muted">esc</kbd>
        </div>
        <ul role="listbox" className="max-h-80 overflow-y-auto">
          {filtered.length === 0 ? (
            <li className="px-3 py-3 text-xs text-dash-muted">
              No sites match &quot;{query}&quot;.
            </li>
          ) : (
            filtered.map((s, i) => {
              const active = s.slug === currentSlug;
              const count = draftCounts[s.id] ?? 0;
              return (
                <li key={s.id}>
                  <button
                    ref={(el) => {
                      itemRefs.current[i] = el;
                    }}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setFocusIndex(i)}
                    onClick={() => onSelect(s.slug)}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${
                      i === focusIndex ? 'bg-dash-bg' : ''
                    } ${active ? 'text-accent' : 'text-dash-secondary'}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm truncate">{s.name}</div>
                      <div className="text-[10px] font-mono text-dash-muted truncate">
                        /{s.slug}
                        {s.domain ? ` · ${s.domain}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {count > 0 ? (
                        <span className="text-[10px] font-mono text-warning bg-warning/[0.15] border border-warning/40 rounded px-1.5">
                          {count} draft{count === 1 ? '' : 's'}
                        </span>
                      ) : null}
                      <StatusBadge status={s.status} />
                    </div>
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <div className="border-t border-dash-border p-2 text-right">
          <button
            type="button"
            onClick={onManageSites}
            className="text-xs font-mono uppercase text-accent hover:underline"
          >
            Manage sites
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function filterSites(sites: Site[], q: string): Site[] {
  const term = q.trim().toLowerCase();
  if (!term) return sites;
  return sites.filter(
    (s) =>
      s.name.toLowerCase().includes(term) ||
      s.slug.toLowerCase().includes(term) ||
      (s.domain ?? '').toLowerCase().includes(term),
  );
}
